/**
 * Channels, the read-only campaign board, and the funnel.
 *
 * The channel table has three revenue columns on purpose. They will not agree and they are
 * not made to: one is what the bank saw, one is a model's allocation of it, one is what a
 * platform says about itself. The line under the board is the reality check, and it is the
 * single most useful sentence on the page.
 *
 * The board is read only at launch. Pause, resume and budget changes go through Ads
 * Manager for the first sixty days after cutover, then get decided with data on how often
 * the owner actually wanted to act from here.
 */
import { useState } from 'react';
import type { WindowKind } from '@elutide/shared';
import { Card, DataTable, EmptyState, ErrorState, MoneyText, Pill, Skeleton, type Column } from '@/components';
import { formatPercent } from '@/lib/format';
import { WindowChips } from '@/features/finance/Overview';
import { useChannels } from './api';
import { FreshnessStrip } from './Acquisition';
import type { CampaignRow, ChannelRow, FunnelStep } from './types';
import '@/features/finance/finance.css';

export function ChannelsPage() {
  const [window, setWindow] = useState<WindowKind>('30d');
  const query = useChannels(window);

  if (query.isLoading) return <Skeleton shape="card" />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        message="Channels could not be loaded."
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const data = query.data;
  const check = data.blended_reality_check;
  const blended =
    check.revenue_net_cents === null || check.paid_media_spend_cents === 0
      ? null
      : check.revenue_net_cents / check.paid_media_spend_cents;
  const claimed =
    check.platform_claimed_cents === null || check.paid_media_spend_cents === 0
      ? null
      : check.platform_claimed_cents / check.paid_media_spend_cents;

  return (
    <div className="o-stack">
      <WindowChips value={window} onChange={setWindow} />

      <Card title="Channels" basis="model" action={<FreshnessStrip sources={data.freshness} />}>
        <ChannelsTable rows={data.items} />
        <p className="o-muted">{data.overlap_note}</p>
      </Card>

      <Card
        title="Campaign board"
        basis="estimate"
        action={<Pill tone="neutral">read only</Pill>}
      >
        <CampaignBoard rows={data.campaigns} />
        <p className="o-muted">
          Blended ROAS for the same window, on what the bank saw:{' '}
          <b>{blended === null ? 'no data' : `${blended.toFixed(1)}x`}</b>. The platforms between
          them claim <b>{claimed === null ? 'no data' : `${claimed.toFixed(1)}x`}</b>. That gap is
          the reason both numbers are on this page.
        </p>
      </Card>

      <Card title="Funnel" basis="snapshot">
        <Funnel steps={data.funnel.steps} note={data.funnel.note} available={data.funnel.available} />
        {data.funnel.woo_paid_revenue ? (
          <div className="o-kv o-kv--total">
            <span className="o-kv__label">Paid revenue (WooCommerce, the bank)</span>
            <span className="o-kv__value">
              {data.funnel.woo_paid_revenue.value === null ? (
                <span className="o-muted">no data</span>
              ) : (
                <MoneyText cents={data.funnel.woo_paid_revenue.value} showCents={false} />
              )}
            </span>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */

export function ChannelsTable({ rows }: { rows: ChannelRow[] }) {
  const columns: readonly Column<ChannelRow>[] = [
    { id: 'channel', header: 'Channel', priority: 1, render: (r) => r.channel },
    { id: 'orders', header: 'Orders', priority: 2, align: 'right', render: (r) => r.orders ?? '' },
    {
      id: 'spend',
      header: 'Spend',
      priority: 1,
      align: 'right',
      money: true,
      render: (r) => (r.spend_cents === 0 ? <span className="o-muted">n/a</span> : <MoneyText cents={r.spend_cents} showCents={false} />),
    },
    {
      id: 'modeled',
      header: 'Revenue, TM modeled',
      priority: 1,
      align: 'right',
      money: true,
      render: (r) =>
        r.modeled_revenue_cents === null ? <span className="o-muted">no data</span> : <MoneyText cents={r.modeled_revenue_cents} showCents={false} />,
    },
    {
      id: 'claimed',
      header: 'Revenue, platform claimed',
      priority: 3,
      align: 'right',
      money: true,
      render: (r) =>
        r.claimed_revenue_cents === null ? <span className="o-muted">n/a</span> : <MoneyText cents={r.claimed_revenue_cents} showCents={false} />,
    },
    {
      id: 'roas',
      header: 'ROAS, modeled',
      priority: 2,
      align: 'right',
      money: true,
      render: (r) =>
        r.roas_modeled === null ? (
          <span className="o-muted">n/a</span>
        ) : (
          <span className={r.roas_modeled < 1 ? 'o-kpi__value--crit' : undefined}>{`${r.roas_modeled.toFixed(1)}x`}</span>
        ),
    },
    {
      id: 'claim',
      header: 'Claim ratio',
      priority: 4,
      align: 'right',
      money: true,
      render: (r) => (r.claim_ratio === null ? <span className="o-muted">n/a</span> : `${r.claim_ratio.toFixed(2)}x`),
    },
    { id: 'new', header: 'New customers', priority: 3, align: 'right', render: (r) => r.new_customers ?? '' },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.channel}
      caption="Revenue and spend by channel, on three bases"
      emptyState={<EmptyState message="No channel data for this window." />}
      allowHorizontalScroll
    />
  );
}

/* ------------------------------------------------------------------ */

export function CampaignBoard({ rows }: { rows: CampaignRow[] }) {
  const columns: readonly Column<CampaignRow>[] = [
    { id: 'campaign', header: 'Campaign', priority: 1, render: (r) => r.campaign_name ?? r.campaign_id },
    { id: 'platform', header: 'Platform', priority: 2, render: (r) => <Pill tone="neutral">{r.platform}</Pill> },
    {
      id: 'spend',
      header: 'Spend',
      priority: 1,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.spend_cents} showCents={false} />,
    },
    { id: 'impressions', header: 'Impressions', priority: 4, align: 'right', render: (r) => r.impressions },
    { id: 'clicks', header: 'Clicks', priority: 3, align: 'right', render: (r) => r.clicks },
    { id: 'ctr', header: 'CTR', priority: 3, align: 'right', render: (r) => formatPercent(r.ctr) },
    {
      id: 'cpm',
      header: 'CPM',
      priority: 5,
      align: 'right',
      money: true,
      render: (r) => (r.cpm_cents === null ? '' : <MoneyText cents={r.cpm_cents} showCents />),
    },
    { id: 'purchases', header: 'Purchases, claimed', priority: 2, align: 'right', render: (r) => r.platform_conversions },
    {
      id: 'roas',
      header: 'ROAS, claimed',
      priority: 2,
      align: 'right',
      money: true,
      render: (r) => (r.platform_roas === null ? <span className="o-muted">n/a</span> : `${r.platform_roas.toFixed(1)}x`),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => `${r.platform}:${r.campaign_id}`}
      caption="Campaign performance as the platform reports it"
      emptyState={<EmptyState message="No campaigns reported. The ad sources may not be connected." />}
      allowHorizontalScroll
    />
  );
}

/* ------------------------------------------------------------------ */

const STEP_LABELS: Record<string, string> = {
  sessions: 'Sessions',
  add_to_cart: 'Add to cart',
  checkout: 'Checkout begun',
  ga4_transactions: 'Transactions, as GA4 counts them',
};

export function Funnel({ steps, note, available }: { steps: FunnelStep[]; note: string; available: boolean }) {
  const first = steps[0]?.value ?? null;
  return (
    <>
      {steps.map((step) => {
        const share = first && step.value !== null && first > 0 ? step.value / first : null;
        return (
          <div key={step.step} className="o-kv">
            <span className="o-kv__label">
              {STEP_LABELS[step.step] ?? step.step.replace(/_/g, ' ')}{' '}
              <Pill tone="neutral">{step.source}</Pill>
            </span>
            <span className="o-kv__value">
              {step.value === null ? (
                <span className="o-muted">unknown</span>
              ) : (
                <>
                  {step.value.toLocaleString('en-US')}
                  {share !== null ? <span className="o-muted"> {formatPercent(share)}</span> : null}
                </>
              )}
            </span>
          </div>
        );
      })}
      <p className={available ? 'o-muted' : 'o-card__error'}>{note}</p>
    </>
  );
}
